// src/util/includeScripts.js
import React from 'react';
import { Helmet } from 'react-helmet-async';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

export const IncludeScripts = props => {
  const { maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics || {};
  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  const mapLibraries = [];
  const analyticsLibraries = [];
  const brandLibraries = [];

  // 1) CSS remoto (proxy same-origin -> /brand/*)

// dentro de IncludeScripts()
  brandLibraries.push(<script key="jquery" src="/brand/jquery-3.7.1.js" defer />);
  brandLibraries.push(<script key="funciones" src="/brand/funciones-fut.js" defer />);
  brandLibraries.push(<link key="fa" href="/brand/fontawesome/css/fontawesome.css" rel="stylesheet" />);


  brandLibraries.push(<link key="futura-ext" href="/brand/futura-ext.css" rel="stylesheet" />);
  brandLibraries.push(<link key="auth-local" href="/brand-local/auth-overrides.css" rel="stylesheet" />)




  // 1b) JS local (NO inline, cumple CSP)
  // brandLibraries.push(<script key="auth-local-js" src="/brand-local/auth-overrides.js" />); 


  // 2) Micro-parches (ganan la cascada y evitan el gris del hero/modal)
  const brandFixes = (
    <style key="brand-fixes">{`
      /* --- Barra de pestañas (User/Tab Nav) morada como en dev --- */
      /* Subimos la especificidad a 0,2,1 para ganar a [class^="..."][class] del CSS remoto */
      nav[class^="TabNavHorizontal_root__"][class],
      nav[class*="TabNavHorizontal_root__"][class] {
        background: var(--marketplaceColor, #5d17eb) !important;
        color: var(--colorWhite, #fff) !important;
        border-radius: 25px !important;
      }
      nav[class^="TabNavHorizontal_root__"][class] a,
      nav[class^="TabNavHorizontal_root__"][class] button,
      nav[class*="TabNavHorizontal_root__"][class] a,
      nav[class*="TabNavHorizontal_root__"][class] button {
        color: var(--colorWhite, #fff) !important;
      }
      [class*="TabNavHorizontal_selectedTabContentDarkSkin__"] {
        border-bottom-color: #adec5d !important;
      }

      /* --- Quitar fondo gris del hero/header de autenticación --- */
      [class*="AuthenticationPage_hero"],
      [class*="AuthenticationPage_header"] {
        background: transparent !important;
      }

      /* --- CTAs: texto blanco SOLO en primarios activos (no disabled) --- */
      button[type="submit"][class*="Button_primaryButton__"]:not([disabled]),
      a[role="button"][class*="Button_primaryButton__"],
      button[type="submit"][class*="buttonPrimaryButton__"]:not([disabled]),
      a[role="button"][class*="buttonPrimaryButton__"] {
        color: #fff !important;
      }

      /* Botón primario DESHABILITADO: contraste oscuro (como en dev) */
      button[type="submit"][class*="Button_primaryButton__"][disabled],
      button[type="submit"][class*="buttonPrimaryButton__"][disabled] {
        color: var(--colorGrey700, #000) !important;
      }
    `}</style>
  );

  
  // 3) Map libs
  if (isMapboxInUse) {
    mapLibraries.push(<script key="mapboxSDK" src="/static/scripts/mapbox/mapbox-sdk.min.js"></script>);
    mapLibraries.push(
      <link key="mapbox_GL_CSS" href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css" rel="stylesheet" crossOrigin />
    );
    mapLibraries.push(
      <script id={MAPBOX_SCRIPT_ID} key="mapbox_GL_JS" src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js" crossOrigin />
    );
  } else if (isGoogleMapsInUse) {
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      />
    );
  }

  // 4) Analytics
  if (googleAnalyticsId && googleAnalyticsId.startsWith('G-')) {
    analyticsLibraries.push(
      <script key="gtag.js" async src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`} crossOrigin />
    );
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // eslint-disable-next-line no-undef
      window.gtag = function gtag(){ dataLayer.push(arguments); };
      // eslint-disable-next-line no-undef
      gtag('js', new Date());
      // eslint-disable-next-line no-undef
      gtag('config', googleAnalyticsId, { cookie_flags: 'SameSite=None;Secure' });
    }
  }
  if (plausibleDomains) {
    analyticsLibraries.push(
      <script key="plausible" defer src="https://plausible.io/js/script.js" data-domain={plausibleDomains} crossOrigin />
    );
  }

  // Mapbox token al cargar la librería
  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    window.mapboxgl.accessToken = mapboxAccessToken;
  }
  const onMapLibLoaded = () => {
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
  };
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags?.scriptTags) {
      const s = addedTags.scriptTags.find(t => [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(t.id));
      if (s) s.addEventListener('load', onMapLibLoaded, { once: true });
    }
  };

  const all = [...brandLibraries, brandFixes, ...mapLibraries, ...analyticsLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{all}</Helmet>;
};
